<template>
  <v-container fluid class="h-100 pa-0">
    <v-row no-gutters v-if="detailLoading" class="loader-container">
      <Loader text="Cargando factura" />
    </v-row>
    <v-row no-gutters class="h-100" v-else-if="bill">
      <v-col cols="12" md="8">
        <header class="d-flex align-center mb-4" style="gap: 1rem">
          <v-icon
            class="pa-2 rounded-pill"
            :style="`--element-color: ${
              bill.estado === 3 ? '#E8F5E9' : '#E3F2FD'
            }`"
            :color="
              bill.estado === 3 ? 'light-green darken-4' : 'blue darken-2'
            "
          >
            {{ bill.estado === 3 ? "check" : "history" }}
          </v-icon>
          <h1 class="mb-0 text-h5 text-md-h5 font-weight-bold">
            Factura {{ bill.descripcion_estado?.toLowerCase() }}
          </h1>
        </header>

        <main class="d-flex flex-column" style="gap: 1rem">
          <div class="d-flex flex-column">
            <h6 class="font-weight-bold text-h6 mb-4">Total de la factura</h6>
            <span class="text-subtitle-1">{{
              formatCurrency(bill.monto_factura)
            }}</span>
          </div>

          <div v-if="bill.monto_cobrado" class="d-flex flex-column">
            <h6 class="font-weight-bold text-h6 mb-4">Monto cobrado</h6>
            <span
              >{{ formatCurrency(bill.monto_cobrado) }} - Cobrado el
              {{ bill?.fecha_monto_cobrado | formatDateShort }}
            </span>
          </div>

          <div v-if="bill.estado !== 3" class="d-flex flex-column">
            <h6 class="font-weight-bold text-h6 mb-4">Monto a cobrar</h6>
            <span class="text-subtitle-1">{{
              formatCurrency(bill.monto_a_cobrar)
            }}</span>
            <span class="text-subtitle-1" v-if="bill.monto_a_cobrar > 0">
              - A cobrar el
              {{ bill.fecha_monto_a_cobrar | formatDateShort }}
            </span>
          </div>

          <div
            v-if="bill.monto_excluido && existRejectedTransactions"
            class="d-flex flex-column"
          >
            <h6 class="font-weight-bold text-h6 mb-4">Monto excluido</h6>
            <span class="text-subtitle-1">{{
              formatCurrency(bill.monto_excluido)
            }}</span>
          </div>

          <div>
            <h6 class="font-weight-bold text-h6 mb-4">
              ¿En que cuenta lo voy a cobrar?
            </h6>
            <div class="d-flex flex-column">
              <span class="text-subtitle-1 font-weight-bold"
                >Nombre y Apellido:
                <span class="font-weight-regular"
                  >{{ bill.datos_facturacion?.nombre_prestador }}
                  {{ bill.datos_facturacion?.apellido_prestador }}</span
                ></span
              >
              <span class="text-subtitle-1 font-weight-bold"
                >CUIT:
                <span class="font-weight-regular">{{
                  bill.datos_facturacion?.cuit
                }}</span></span
              >
              <span class="text-subtitle-1 font-weight-bold"
                >Banco:
                <span class="font-weight-regular">{{
                  bill.datos_facturacion?.banco
                }}</span></span
              >
              <span class="text-subtitle-1 font-weight-bold"
                >CBU:
                <span class="font-weight-regular">{{
                  bill.datos_facturacion?.cbu
                }}</span></span
              >
            </div>
          </div>

          <div v-if="bill.transacciones?.length > 0">
            <h6 class="font-weight-bold text-h6 mb-4">Servicios facturados</h6>

            <div class="d-flex flex-column" style="gap: 1rem">
              <BillServiceCard
                v-for="transaction of bill.transacciones"
                :key="transaction.id"
                :transaction="transaction"
              />
            </div>
          </div>

          <section class="d-flex flex-column" style="gap: 1.5rem">
            <div v-if="bill.archivo_comprobantes?.length > 0">
              <h6 class="font-weight-bold text-h6 mb-4">
                Comprobantes de pago
              </h6>
              <div class="d-flex flex-column" style="gap: 1rem">
                <ProofItem
                  v-for="(proof, index) of bill.archivo_comprobantes"
                  :key="index"
                  :item="proof"
                  show-amount
                />
              </div>
            </div>

            <div v-if="bill.archivo_factura">
              <h6 class="font-weight-bold text-h6 mb-4">Facturas asociada</h6>
              <ProofItem
                variant="invoice"
                show-amount
                :item="{
                  file: bill.archivo_factura,
                  filename: bill.filename,
                  amount: bill.monto_factura,
                  created_time: bill.created_time,
                }"
              />
            </div>
          </section>
        </main>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState } from "vuex";

import BillServiceCard from "../components/BillServiceCard.vue";
import ProofItem from "../components/ProofItem.vue";
import Loader from "../components/Loader.vue";

export default {
  components: { BillServiceCard, ProofItem, Loader },
  props: ["id"],
  mounted() {
    this.$store.commit("set_app_title", "Factura #" + this.id);
  },
  methods: {
    async fetchInvoiceDetail(id) {
      try {
        await this.$store.dispatch("invoice/getBillDetail", id);
      } catch (error) {
        const status = error.response ? error.response.status : null;

        if (status === 404) {
          this.$router.push({ name: "404", params: { resource: "factura" } });
        } else if (status !== 401) {
          this.$router.push({ name: "network-issue" });
        }
      }
    },
    formatCurrency(amount) {
      if (!amount) return "$ 0,00";

      const newAmount =
        typeof amount === "string"
          ? parseFloat(amount.replace(",", "."))
          : amount;

      const formatter = new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      return formatter.format(newAmount);
    },
  },
  computed: {
    ...mapState("invoice", {
      detailLoading: (state) => state.isDetailLoading,
      bill: (state) => state.bill,
    }),

    existRejectedTransactions() {
      return this.bill?.transacciones.some((t) => t.status === 5);
    },
  },
  watch: {
    id: {
      handler(newId) {
        this.fetchInvoiceDetail(newId);
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.loader-container {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 959px) {
  .left-col {
    width: 100%;
  }
}

@media (min-width: 960px) {
  .left-col {
    width: 65%;
  }
}
.short-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.v-icon {
  background-color: var(--element-color);
}
</style>
